import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';

import { Box, Button, DialogActions, DialogContent, FormControl, RadioGroup } from '@mui/material';

import { Order } from '@luxuryescapes/contract-svc-order';

import { CollectionReasonKey } from '~/consts/refund';

import { CollectionReasonGroup } from '../../types';
import { getReasonsByItemType } from '../Utils/RefundUtils';

import { RefundReasonCategorySectionTitle } from './components/RefundReasonCategorySectionTitle';
import { RefundReasonRadio } from './components/RefundReasonRadio';

interface Props {
  itemType: string;
  isRefundable: boolean;
  order: Order.Order;
  handleReasonSubmission: (key: CollectionReasonKey) => void;
  isItemInPaymentSchedule?: boolean;
  isBNBL: boolean;
}

export default function RefundReasons({
  itemType,
  order,
  isRefundable,
  handleReasonSubmission,
  isItemInPaymentSchedule = false,
  isBNBL,
}: Props) {
  const { refundTypes: allRefundTypes, reasons } = getReasonsByItemType(
    itemType,
    order,
    isRefundable,
    false,
    isItemInPaymentSchedule,
  );
  // Only show EXPIRED_BNBL_PACKAGE option for BNBL items
  const refundTypes = isBNBL ? allRefundTypes : allRefundTypes.filter((key) => key !== 'EXPIRED_BNBL_PACKAGE');
  const [selectedReason, setSelectedReason] = useState<CollectionReasonKey>(undefined);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedReason(event.target.value as CollectionReasonKey);
  };

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      handleReasonSubmission(selectedReason);
    },
    [handleReasonSubmission, selectedReason],
  );

  const refundReasonsCategories = useMemo<Record<CollectionReasonGroup, Array<CollectionReasonKey>>>(() => {
    const standardRefundReasonsKeys: Array<CollectionReasonKey> = [];
    const complexRefundReasonsKeys: Array<CollectionReasonKey> = [];
    const uncategorizedRefundReasonsKeys: Array<CollectionReasonKey> = [];

    for (let i = 0; i < refundTypes.length; i++) {
      const key = refundTypes[i];
      const group = reasons[key].group;

      if (group === CollectionReasonGroup.STANDARD_REFUND && !reasons[key].hidden) {
        standardRefundReasonsKeys.push(key);
      } else if (group === CollectionReasonGroup.COMPLEX_REFUND && !reasons[key].hidden) {
        complexRefundReasonsKeys.push(key);
      } else if (group === CollectionReasonGroup.UNCATEGORIZED && !reasons[key].hidden) {
        uncategorizedRefundReasonsKeys.push(key);
      }
    }

    return {
      [CollectionReasonGroup.STANDARD_REFUND]: standardRefundReasonsKeys,
      [CollectionReasonGroup.COMPLEX_REFUND]: complexRefundReasonsKeys,
      [CollectionReasonGroup.UNCATEGORIZED]: uncategorizedRefundReasonsKeys,
    };
  }, [refundTypes, reasons]);

  return (
    <>
      <DialogContent dividers>
        <form id="refund-reason-form" onSubmit={handleSubmit}>
          <FormControl required>
            <RadioGroup value={selectedReason} onChange={handleChange}>
              {refundReasonsCategories[CollectionReasonGroup.STANDARD_REFUND].length > 0 && (
                <Box mt={0}>
                  <RefundReasonCategorySectionTitle
                    severity="success"
                    title="Standard Refund"
                    helperText="(LE will not pay the hotel)"
                  />
                  <Box paddingLeft={2}>
                    {refundReasonsCategories[CollectionReasonGroup.STANDARD_REFUND].map((refundReasonKey) => {
                      return (
                        <RefundReasonRadio
                          key={refundReasonKey}
                          refundReasonKey={refundReasonKey}
                          label={reasons[refundReasonKey]?.label ?? reasons[refundReasonKey].key}
                          description={reasons[refundReasonKey].description}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
              {/* end of vendor not paid for booking */}

              {refundReasonsCategories[CollectionReasonGroup.COMPLEX_REFUND].length > 0 && (
                <Box mt={1}>
                  <RefundReasonCategorySectionTitle
                    severity="error"
                    title="Complex Refund"
                    helperText="(LE will pay the hotel a custom amount)"
                  />
                  <Box paddingLeft={2}>
                    {refundReasonsCategories[CollectionReasonGroup.COMPLEX_REFUND].map((refundReasonKey) => {
                      return (
                        <RefundReasonRadio
                          key={refundReasonKey}
                          refundReasonKey={refundReasonKey}
                          label={reasons[refundReasonKey]?.label ?? reasons[refundReasonKey].key}
                          description={reasons[refundReasonKey].description}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
              {/* end of vendor paid custom amount */}

              {refundReasonsCategories[CollectionReasonGroup.UNCATEGORIZED].length > 0 && (
                <Box mt={1}>
                  <RefundReasonCategorySectionTitle title="Other" severity="warning" />
                  <Box paddingLeft={2}>
                    {refundReasonsCategories[CollectionReasonGroup.UNCATEGORIZED].map((refundReasonKey) => {
                      return (
                        <RefundReasonRadio
                          key={refundReasonKey}
                          refundReasonKey={refundReasonKey}
                          label={reasons[refundReasonKey]?.label ?? reasons[refundReasonKey].key}
                          description={reasons[refundReasonKey].description}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
              {/* end of other (uncategorized) */}
            </RadioGroup>
          </FormControl>
        </form>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" type="submit" form="refund-reason-form">
          Continue
        </Button>
      </DialogActions>
    </>
  );
}
